import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Mail, Shield, MoreVertical, Trash2, Truck } from 'lucide-react';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import PlacesAutocompleteInput from '../../components/ui/places-autocomplete-input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import RoleBadge from '../../components/RoleBadge';
import UserRow from '../../components/UserRow';
import { toast } from 'sonner';
import { isAdminLike } from '../../utils/roles';
import { isInsidePacContainer } from '../../lib/googleMapsLoader';

const TeamMembers = () => {
  const { role, isOwner } = useRole();
  const isAdmin = isAdminLike(role);
  const { companyId } = useCompany();
  const { user: currentUser } = useAuth();

  const isCompanyAdmin = isOwner || isAdmin;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingInvites, setPendingInvites] = useState([]);

  const activeUsers = users.filter((u) => u.hasSetPassword === true);
  const pendingUsers = users.filter((u) => !u.hasSetPassword);

  // Manual user creation modal
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', role: 'sales' });

  // Edit / delete state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: '' });

  // Per-technician dispatch defaults editor
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [dispatchForm, setDispatchForm] = useState({
    defaultStartAddress: '',
    defaultMaxStops: 6,
    defaultHalfDay: false,
    // Phase 1 end-location fields (iter_225 backend, iter_228 UI).
    defaultHomeAddress: '',
    defaultEndAddress: '',
    endLocationMode: 'office',     // 'office' | 'home' | 'custom' | 'none'
  });
  const [dispatchSaving, setDispatchSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    try {
      if (!companyId) {
        setUsers([]);
        setPendingInvites([]);
        return;
      }

      // Source list from /companies/{companyId}/users and join with /users/{uid} for profile fields.
      const membershipsSnapshot = await getDocs(collection(db, 'companies', companyId, 'users'));
      const memberships = membershipsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const joined = await Promise.all(
        memberships.map(async (m) => {
          try {
            const profileSnap = await getDoc(doc(db, 'users', m.id));
            const profile = profileSnap.exists() ? profileSnap.data() : {};
            return {
              // Spread membership first (has hasSetPassword mirrored there)
              // then profile — profile wins if both have the field.
              ...m,
              ...profile,
            };
          } catch (e) {
            // If profile read fails under security rules, fall back to
            // membership doc data (which has role + hasSetPassword mirrored).
            return { ...m };
          }
        })
      );

      setUsers(joined);

      // Self-heal: if any membership is missing a `name`, trigger a one-shot
      // admin backfill (idempotent on the server) and reload once. This
      // repairs the legacy accept-flow gap where the membership doc never
      // received the name. The endpoint is 403 for non-admins; we swallow
      // errors since it's a best-effort heal.
      try {
        const needsBackfill = joined.some(
          (u) => !(u.name || u.displayName) && u.email
        );
        if (needsBackfill && !window.__kroNamesBackfilled) {
          window.__kroNamesBackfilled = true; // run once per session
          const currentUser = auth.currentUser;
          if (currentUser) {
            const idToken = await currentUser.getIdToken();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(
              `${backendUrl}/api/admin/memberships/backfill-names`,
              { method: 'POST', headers: { Authorization: `Bearer ${idToken}` } },
            );
            if (res.ok) {
              const data = await res.json().catch(() => ({}));
              if (data?.updated > 0) {
                // Reload the membership list so the freshly-backfilled names appear.
                const membershipsSnapshot2 = await getDocs(collection(db, 'companies', companyId, 'users'));
                const memberships2 = membershipsSnapshot2.docs.map((d) => ({ id: d.id, ...d.data() }));
                const joined2 = await Promise.all(
                  memberships2.map(async (m) => {
                    try {
                      const profileSnap = await getDoc(doc(db, 'users', m.id));
                      const profile = profileSnap.exists() ? profileSnap.data() : {};
                      return { ...m, ...profile };
                    } catch {
                      return { ...m };
                    }
                  })
                );
                setUsers(joined2);
              }
            }
          }
        }
      } catch {
        // Non-fatal — names will simply fall back to email local-part.
      }

      // Fetch pending v2 invites — admin-only endpoint; 403 is expected for non-admins
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken();
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${backendUrl}/api/admin/invites-v2/pending`, {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (res.ok) {
            const inviteData = await res.json();
            if (inviteData.success) {
              setPendingInvites(inviteData.invites || []);
            }
          }
        }
      } catch {
        // Non-fatal: pending invites tab simply shows empty
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUserSubmit = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name || !createForm.role) {
      toast.error('Please provide name, email, and role');
      return;
    }

    const loadingToast = toast.loading('Creating user...');

    try {
      const current = auth.currentUser;
      if (!current) {
        toast.dismiss(loadingToast);
        toast.error('You must be logged in as an admin');
        return;
      }

      const idToken = await current.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Use backend-driven invite flow v2 instead of legacy manual user creation.
      const response = await fetch(`${backendUrl}/api/admin/invites-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          name: createForm.name,
          email: createForm.email,
          role: createForm.role,
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (err) {
        console.error('JSON parse failed:', err);
        toast.dismiss(loadingToast);
        toast.error('Unexpected server response. Check backend logs.');
        return;
      }

      toast.dismiss(loadingToast);

      if (!response.ok || !data.success) {
        toast.error(data.message || 'Failed to send invitation');
        return;
      }

      if (data.emailSent === false) {
        toast.warning('Invite created, but email failed to send. Use "Resend" in the Pending tab to retry.');
      } else {
        toast.success('Invitation sent. The team member will receive an email to join your company.');
      }
      setCreateUserModalOpen(false);
      setCreateForm({ name: '', email: '', role: 'sales' });
      loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error creating user manually:', error);
      toast.error('Failed to create user');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({ role: user.role || 'sales' });
    setEditModalOpen(true);
  };

  // ── Dispatch defaults editor ───────────────────────────────────────────────
  // Owner/admin opens the dialog from a technician's row. Pre-fills from the
  // membership doc; partial-merge saved via PUT /api/team/{uid}/dispatch-defaults.
  // Non-technicians never see the menu item.
  const handleEditDispatchDefaults = (user) => {
    setSelectedUser(user);
    setDispatchForm({
      defaultStartAddress: user.defaultStartAddress || '',
      defaultMaxStops:
        typeof user.defaultMaxStops === 'number' ? user.defaultMaxStops : 6,
      defaultHalfDay: !!user.defaultHalfDay,
      defaultHomeAddress: user.defaultHomeAddress || '',
      defaultEndAddress:  user.defaultEndAddress  || '',
      endLocationMode:   (user.endLocationMode || 'office').toLowerCase(),
    });
    setDispatchModalOpen(true);
  };

  const handleSaveDispatchDefaults = async (e) => {
    e.preventDefault();
    if (!selectedUser || dispatchSaving) return;

    const ms = parseInt(dispatchForm.defaultMaxStops, 10);
    if (!Number.isInteger(ms) || ms < 1 || ms > 20) {
      toast.error('Max stops must be between 1 and 20');
      return;
    }

    setDispatchSaving(true);
    const loadingToast = toast.loading('Saving dispatch defaults…');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(
        `${backendUrl}/api/team/${selectedUser.id}/dispatch-defaults?companyId=${encodeURIComponent(companyId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            defaultStartAddress: (dispatchForm.defaultStartAddress || '').trim(),
            defaultMaxStops: ms,
            defaultHalfDay: !!dispatchForm.defaultHalfDay,
            // Phase 1 end-location persistence. Empty string clears the
            // stored value (backend `.strip()` accepts empties).
            defaultHomeAddress: (dispatchForm.defaultHomeAddress || '').trim(),
            defaultEndAddress:  (dispatchForm.defaultEndAddress  || '').trim(),
            endLocationMode:    (dispatchForm.endLocationMode    || 'office').toLowerCase(),
          }),
        },
      );
      toast.dismiss(loadingToast);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.detail || 'Failed to save dispatch defaults');
        return;
      }
      toast.success('Dispatch defaults saved');
      setDispatchModalOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (err) {
      toast.dismiss(loadingToast);
      console.error('[TeamMembers] save dispatch defaults failed:', err);
      toast.error('Failed to save dispatch defaults');
    } finally {
      setDispatchSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) return;

    const loadingToast = toast.loading('Updating user role...');

    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/update-user-role/${selectedUser.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ role: editForm.role }),
      });

      const data = await response.json();
      toast.dismiss(loadingToast);

      if (!response.ok || !data.success) {
        toast.error(data.message || 'Failed to update user role');
        return;
      }

      toast.success('User role updated successfully');
      setEditModalOpen(false);
      loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };
  const handleResendPasswordEmail = async (user) => {
    // Distinguish pending state:
    // - hasSetPassword === false (explicit)  → legacy create-user-manual user → send password reset
    // - hasSetPassword absent/null            → stuck v2 invite user → resend v2 invite link
    const isLegacyPending = user.hasSetPassword === false;
    const loadingToast = toast.loading(
      isLegacyPending ? 'Resending password setup email...' : 'Resending invite...'
    );

    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      let response;
      if (isLegacyPending) {
        // Legacy flow: send Firebase password reset link
        response = await fetch(`${backendUrl}/api/admin/resend-password-email/${user.id}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}` },
        });
      } else {
        // v2 invite flow: regenerate token and resend the invite email
        response = await fetch(
          `${backendUrl}/api/admin/invites-v2/resend-by-uid?uid=${encodeURIComponent(user.id)}`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
      }

      let data;
      try {
        data = await response.json();
      } catch (err) {
        console.error('JSON parse failed:', err);
      }

      toast.dismiss(loadingToast);

      if (!response.ok || !data?.success) {
        toast.error(data?.message || 'Failed to resend email');
        return;
      }

      toast.success(isLegacyPending ? 'Password setup email resent' : 'Invite resent');
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error resending email:', error);
      toast.error('Failed to resend email');
    }
  };

  const handleHardDeletePendingUser = async (user) => {
    const loadingToast = toast.loading('Deleting user...');

    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/admin/delete-user-hard/${user.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      let data;
      try {
        data = await response.json();
      } catch (err) {
        console.error('JSON parse failed:', err);
      }

      toast.dismiss(loadingToast);

      if (!response.ok || !data?.success) {
        toast.error(data?.message || 'Failed to delete user');
        return;
      }

      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error hard-deleting user:', error);
      toast.error('Failed to delete user');
    }
  };



  const handleCancelInvite = async (invite) => {
    const loadingToast = toast.loading('Cancelling invite...');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/invites-v2/${invite.inviteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json();
      toast.dismiss(loadingToast);
      if (!response.ok || !data.success) {
        toast.error(data.message || 'Failed to cancel invite');
        return;
      }
      toast.success('Invite cancelled');
      loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error cancelling invite:', error);
      toast.error('Failed to cancel invite');
    }
  };

  const handleResendInvite = async (invite) => {
    const loadingToast = toast.loading('Resending invite...');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/invites-v2/${invite.inviteId}/resend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json();
      toast.dismiss(loadingToast);
      if (!response.ok || !data.success) {
        toast.error(data.message || 'Failed to resend invite');
        return;
      }
      if (data.emailSent === false) {
        toast.warning('Token refreshed but email failed to send. Check SendGrid configuration.');
      } else {
        toast.success('Invite resent successfully');
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error resending invite:', error);
      toast.error('Failed to resend invite');
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    if (selectedUser.id === currentUser?.uid) {
      toast.error('You cannot remove your own account.');
      setDeleteModalOpen(false);
      setSelectedUser(null);
      return;
    }

    const loadingToast = toast.loading('Removing user...');

    try {
      const idToken = await auth.currentUser.getIdToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // For now, keep using soft delete for active users only
      const response = await fetch(`${backendUrl}/api/delete-user/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      const data = await response.json();
      toast.dismiss(loadingToast);

      if (!response.ok || !data.success) {
        toast.error(data.message || 'Failed to remove user');
        return;
      }

      toast.success('User removed successfully');
      setDeleteModalOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Error deleting user:', error);
      toast.error('Failed to remove user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <div>
            <h2 className="text-2xl font-bold text-secondary">Team Members</h2>
            <p className="text-sm text-muted-foreground">
              {isAdmin ? 'Add and manage your team members' : 'View your team members'}
            </p>
          </div>
        </div>

        {isAdmin && (
          <Button
            onClick={() => setCreateUserModalOpen(true)}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
        )}
      </div>

      {/* Admin view: Active vs Pending tabs */}
      {isAdmin ? (
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList>
            <TabsTrigger value="active">Active ({activeUsers.length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({pendingUsers.length + pendingInvites.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* ── Desktop table (>= lg) ─────────────────────────────── */}
              <table className="hidden lg:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeUsers.map((user) => {
                    const isCurrent = user.id === currentUser?.uid;
                    // Do not allow managing your own membership, and never allow editing another admin-like user
                    const canManage = !isCurrent && !isAdminLike(user.role);

                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              {user.photoURL ? (
                                <img
                                  src={user.photoURL}
                                  alt={user.displayName || user.email}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-primary font-semibold">
                                  {(user.displayName || user.name || user.email || 'U')[0].toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.displayName || user.name || (user.email ? user.email.split('@')[0] : 'No name')}
                                {isCurrent && <span className="ml-2 text-xs text-gray-500">(You)</span>}
                              </div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <RoleBadge role={user.role} size="sm" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.createdAt
                            ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {canManage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" aria-label={`Actions for ${user.email}`}>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!isCurrent && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                      <Shield className="h-4 w-4 mr-2" />
                                      Edit Role
                                    </DropdownMenuItem>
                                    {(user.role || '').toLowerCase() === 'technician' && (
                                      <DropdownMenuItem
                                        onClick={() => handleEditDispatchDefaults(user)}
                                        data-testid={`team-member-dispatch-defaults-${user.id}`}
                                      >
                                        <Truck className="h-4 w-4 mr-2" />
                                        Dispatch defaults
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteUser(user)}
                                      className="text-red-600"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Remove User
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* ── Mobile card list (< lg) ──────────────────────────── */}
              <div className="lg:hidden divide-y divide-gray-200">
                {activeUsers.map((user) => {
                  const isCurrent = user.id === currentUser?.uid;
                  const canManage = !isCurrent && !isAdminLike(user.role);
                  const joined = user.createdAt
                    ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                    : null;

                  return (
                    <div
                      key={user.id}
                      className="flex items-start gap-3 px-4 py-3"
                      data-testid={`team-member-card-${user.id}`}
                    >
                      {/* Avatar */}
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName || user.email}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-primary font-semibold">
                            {(user.displayName || user.name || user.email || 'U')[0].toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Name / email / role / joined — stacked + truncated */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {user.displayName || user.name || (user.email ? user.email.split('@')[0] : 'No name')}
                          </p>
                          {isCurrent && (
                            <span className="text-[11px] text-gray-500 shrink-0">(You)</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <RoleBadge role={user.role} size="sm" />
                          {joined && (
                            <span className="text-[11px] text-gray-400">Joined {joined}</span>
                          )}
                        </div>
                      </div>

                      {/* Kebab — tap target 44px, always-visible on touch */}
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-11 w-11 shrink-0"
                              data-testid={`team-member-actions-${user.id}`}
                            >
                              <MoreVertical className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditUser(user)}>
                              <Shield className="h-4 w-4 mr-2" />
                              Edit Role
                            </DropdownMenuItem>
                            {(user.role || '').toLowerCase() === 'technician' && (
                              <DropdownMenuItem
                                onClick={() => handleEditDispatchDefaults(user)}
                                data-testid={`team-member-dispatch-defaults-mobile-${user.id}`}
                              >
                                <Truck className="h-4 w-4 mr-2" />
                                Dispatch defaults
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </div>

              {activeUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No active team members</h3>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="pending">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* ── Pending v2 invites (email sent, not yet accepted) ── */}
                  {pendingInvites.map((invite) => (
                    <tr key={invite.inviteId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-sm">
                              {(invite.name || invite.email || 'I')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {invite.name || invite.email.split('@')[0]}
                              </span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                Invite Sent
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">{invite.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RoleBadge role={invite.role} size="sm" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invite.createdAt
                          ? new Date(invite.createdAt).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResendInvite(invite)}
                            data-testid={`resend-invite-${invite.inviteId}`}
                          >
                            Resend
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelInvite(invite)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`cancel-invite-${invite.inviteId}`}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {/* ── Legacy pending users (hasSetPassword === false) ── */}
                  {pendingUsers.map((user) => {
                    // Safety check: if the join produced no email, this membership
                    // doc has no real auth account attached. Show a warning row
                    // with only a Remove action (no Resend — there's no one to email).
                    const isOrphaned = !user.email && !user.displayName && !user.name;
                    return (
                    <tr key={user.id} className={`hover:bg-gray-50 ${isOrphaned ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center
                            ${isOrphaned ? 'bg-amber-100' : 'bg-primary/10'}`}>
                            <span className={`font-semibold ${isOrphaned ? 'text-amber-600 text-xs' : 'text-primary'}`}>
                              {isOrphaned ? '?' : (user.displayName || user.name || user.email || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {isOrphaned ? 'Orphaned account' : (user.displayName || user.name || (user.email ? user.email.split('@')[0] : 'No name'))}
                              </span>
                              {isOrphaned && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs
                                  font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                  No auth account
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-400 font-mono text-xs">
                              {isOrphaned ? `uid: ${user.id.slice(0, 16)}…` : user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RoleBadge role={user.role} size="sm" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.createdAt
                          ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          {!isOrphaned && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendPasswordEmail(user)}
                            >
                              Resend Email
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleHardDeletePendingUser(user)}
                          >
                            {isOrphaned ? 'Remove' : 'Delete'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              {pendingUsers.length === 0 && pendingInvites.length === 0 && (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No pending users</h3>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        /* Non-admin view: Read-only list */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName || user.email}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-primary font-semibold">
                            {(user.displayName || user.name || user.email || 'U')[0].toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName || user.name || (user.email ? user.email.split('@')[0] : 'No name')}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <RoleBadge role={user.role} size="sm" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.createdAt
                      ? new Date(user.createdAt.seconds * 1000).toLocaleDateString()
                      : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {users.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
            </div>
          )}
        </div>
      )}

      {/* Create User Modal (manual onboarding) */}
      <Dialog open={createUserModalOpen} onOpenChange={setCreateUserModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Create a user manually. They will receive an email to set their password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUserSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">Full Name</Label>
                <Input
                  id="create-name"
                  placeholder="John Doe"
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-email">Email Address</Label>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <Input
                    id="create-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, email: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="create-role">Role</Label>
                <Select
                  value={createForm.role}
                  onValueChange={(value) =>
                    setCreateForm({ ...createForm, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateUserModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
              >
                Add Team Member
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.displayName || selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(value) =>
                    setEditForm({ ...editForm, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dispatch Defaults Modal — owner/admin only, technician rows only */}
      <Dialog open={dispatchModalOpen} onOpenChange={setDispatchModalOpen}>
        <DialogContent
          data-testid="dispatch-defaults-dialog"
          // Keep the dialog open when the user clicks a Google Places
          // suggestion. Google injects `.pac-container` as a sibling of
          // <body>, so a raw click registers as "outside" to Radix and
          // would otherwise close the dialog before `place_changed` fires.
          onInteractOutside={(e) => {
            if (isInsidePacContainer(e)) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (isInsidePacContainer(e)) e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-[var(--cb-orange)]" />
              Dispatch defaults
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.displayName || selectedUser?.name || selectedUser?.email}
              {' — '}
              These values pre-fill the Build Day sheet every morning. Operators can
              still override per run without changing what&apos;s saved here.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveDispatchDefaults}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="dd-start-address" className="text-sm font-medium">
                  Default start address
                </Label>
                <PlacesAutocompleteInput
                  id="dd-start-address"
                  value={dispatchForm.defaultStartAddress}
                  onChange={(v) =>
                    setDispatchForm((f) => ({ ...f, defaultStartAddress: v }))
                  }
                  placeholder="e.g., 123 Tech Home Rd, Springfield, IL"
                  className="h-9 text-sm"
                  data-testid="dd-start-address-input"
                  maxLength={300}
                />
                <p className="text-xs text-gray-500">
                  Used by Build Day as this technician&apos;s starting point. Falls back
                  to the company depot if left blank.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dd-max-stops" className="text-sm font-medium">
                  Default max stops
                </Label>
                <Input
                  id="dd-max-stops"
                  type="number"
                  min={1}
                  max={20}
                  value={dispatchForm.defaultMaxStops}
                  onChange={(e) =>
                    setDispatchForm((f) => ({ ...f, defaultMaxStops: e.target.value }))
                  }
                  className="h-9 w-24 text-sm"
                  data-testid="dd-max-stops-input"
                />
                <p className="text-xs text-gray-500">
                  Daily capacity. Range 1–20. Overrides the company default.
                </p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={!!dispatchForm.defaultHalfDay}
                  onCheckedChange={(v) =>
                    setDispatchForm((f) => ({ ...f, defaultHalfDay: !!v }))
                  }
                  data-testid="dd-half-day-checkbox"
                />
                <span className="text-sm font-medium">Half day by default</span>
              </label>
              <p className="text-xs text-gray-500 pl-6 -mt-3">
                When on, Build Day halves this technician&apos;s capacity (min 1 stop).
              </p>

              {/* ── End-location section (Phase 1 routing) ────────────────────
                  Lets owner/admin pre-configure where this tech's day-route
                  closes. Build Day picks these up automatically — operators
                  can still override per-day from the Build Day sheet. */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">End-location routing</div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Where does this technician&apos;s day route close? Build Day adds the closing leg to total drive + mileage.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium block">End location mode</label>
                  <div
                    className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 w-full"
                    data-testid="dd-endmode-group"
                  >
                    {[
                      { v: 'office', label: 'Office',  hint: 'Return to Start' },
                      { v: 'home',   label: 'Home',    hint: 'End at Home address' },
                      { v: 'custom', label: 'Custom',  hint: 'End at a specific address' },
                      { v: 'none',   label: 'None',    hint: 'Open route — no return leg' },
                    ].map((opt) => {
                      const active = (dispatchForm.endLocationMode || 'office') === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() =>
                            setDispatchForm((f) => ({ ...f, endLocationMode: opt.v }))
                          }
                          className={`flex-1 h-8 px-2 rounded text-[12px] font-semibold transition-all ${
                            active
                              ? 'bg-white shadow-sm text-gray-900'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                          data-testid={`dd-endmode-${opt.v}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {{
                      office: 'Route closes at the Start address (classic depot loop).',
                      home:   'Route ends at the home address below — useful for last-stop-to-home techs.',
                      custom: 'Route ends at the explicit address below.',
                      none:   'Open route. Drive time after the last stop is not counted.',
                    }[dispatchForm.endLocationMode || 'office']}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium block" htmlFor="dd-home-address">
                    Home address
                    <span className="ml-1 text-[11px] font-normal text-gray-500">(used when End location mode = Home)</span>
                  </label>
                  <Input
                    id="dd-home-address"
                    placeholder="e.g. 500 Maple Ln, Branford, CT 06405"
                    value={dispatchForm.defaultHomeAddress}
                    onChange={(e) =>
                      setDispatchForm((f) => ({ ...f, defaultHomeAddress: e.target.value }))
                    }
                    className="h-9 text-sm"
                    data-testid="dd-home-address-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium block" htmlFor="dd-end-address">
                    Custom end address
                    <span className="ml-1 text-[11px] font-normal text-gray-500">(used when End location mode = Custom)</span>
                  </label>
                  <Input
                    id="dd-end-address"
                    placeholder="e.g. 1234 New Haven Ave, Milford, CT 06460"
                    value={dispatchForm.defaultEndAddress}
                    onChange={(e) =>
                      setDispatchForm((f) => ({ ...f, defaultEndAddress: e.target.value }))
                    }
                    className="h-9 text-sm"
                    data-testid="dd-end-address-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDispatchModalOpen(false)}
                disabled={dispatchSaving}
                data-testid="dd-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={dispatchSaving}
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
                data-testid="dd-save-btn"
              >
                {dispatchSaving ? 'Saving…' : 'Save defaults'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {selectedUser?.displayName || selectedUser?.email}?
            </DialogDescription>
          </DialogHeader>
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-sm text-red-800">
              This action will disable the user&apos;s access to the system. The user will be moved to deleted users and can be restored later if needed.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteConfirm}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMembers;
